import React, { useState, useMemo } from 'react';
import {
  MOIS, MOIS_TRIMESTRIEL, MOIS_SEMESTRIEL, STATUS_LABELS, STATUS_BADGE_CLASS,
  NEEDS_MOTIF, moisActifs, montantAnnuel, initials, formatMontant, exportCSV, printElement
} from '../utils/helpers';
import TableauAnnuel from './TableauAnnuel';

const PAY_TABS = [
  { id: 'prelev_1',    label: 'Prél. J1',    short: 'J1',  filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '1' },
  { id: 'prelev_3',    label: 'Prél. J3',    short: 'J3',  filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '3' },
  { id: 'prelev_5',    label: 'Prél. J5',    short: 'J5',  filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '5' },
  { id: 'prelev_10',   label: 'Prél. J10',   short: 'J10', filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '10' },
  { id: 'prelev_30',   label: 'Prél. J30',   short: 'J30', filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '30' },
  { id: 'trimestriel', label: 'Trimestriel', short: '3M',  filter: a => a.modePaiement === 'Prélèvement auto' && a.periodicite === 'Trimestrielle' },
  { id: 'semestriel',  label: 'Semestriel',  short: '6M',  filter: a => a.modePaiement === 'Prélèvement auto' && a.periodicite === 'Semestrielle' },
  { id: 'annuel_per',  label: 'Annuel',      short: '12M', filter: a => a.modePaiement === 'Prélèvement auto' && a.periodicite === 'Annuelle' },
  { id: 'virement',    label: 'Virement',    short: 'VIR', filter: a => a.modePaiement === 'Virement' },
  { id: 'cheque',      label: 'Chèque',      short: 'CHQ', filter: a => a.modePaiement === 'Chèque' },
];

const STATUS_OPTIONS = [
  { status: 'paye',              label: '✅ Payé',              bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  { status: 'defaut_provision',  label: '⚠️ Défaut paiement',  bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  { status: 'arret_adhesion',    label: '🛑 Arrêt adhésion',   bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
  { status: 'autre_motif',       label: '📝 Autre motif',      bg: '#f5f5f5', color: '#555',    border: '#e0e0e0' },
];

export default function Pointage({ adherents, etablissements, cotisations, selectedYear, setYear, setCotisation, resetAllCotisationsYear, goToAdherent, saveAdherent, ...rest }) {
  const [subView, setSubView] = useState('mensuel');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [payTab, setPayTab] = useState('prelev_1');
  const [statusModal, setStatusModal] = useState(null);
  const [motifText, setMotifText] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkPending, setBulkPending] = useState(null);
  const [bulkMotif, setBulkMotif] = useState('');
  const [montantModal, setMontantModal] = useState(null);
  const [montantValue, setMontantValue] = useState('');
  const [showAllKpi, setShowAllKpi] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmReset, setConfirmReset] = useState(false);

  const validMonths = payTab === 'trimestriel' ? MOIS_TRIMESTRIEL
    : payTab === 'semestriel' ? MOIS_SEMESTRIEL
    : payTab === 'annuel_per' ? null
    : null;

  const yearCots = cotisations[selectedYear] || {};
  const actifs = adherents.filter(a => a.statut !== 'Radié');
  const etabName = (id) => etablissements.find(e => e.id === id)?.nom || '—';

  const getStatus = (adh) => {
    const c = (yearCots[adh.id] || {})[selectedMonth];
    return c ? c.status : 'non_pointe';
  };
  const getMotif = (adh) => {
    const c = (yearCots[adh.id] || {})[selectedMonth];
    return c?.motif || '';
  };

  const currentTabDef = PAY_TABS.find(t => t.id === payTab) || PAY_TABS[0];

  const tabAdherents = useMemo(
    () => actifs.filter(currentTabDef.filter),
    [actifs, payTab] // eslint-disable-line
  );

  const statusCounts = useMemo(() => {
    const c = { all: tabAdherents.length, paye: 0, non_pointe: 0, defaut_provision: 0, arret_adhesion: 0, autre_motif: 0 };
    tabAdherents.forEach(a => {
      const cot = (yearCots[a.id] || {})[selectedMonth];
      const s = cot ? cot.status : 'non_pointe';
      if (c[s] !== undefined) c[s]++;
    });
    return c;
  }, [tabAdherents, yearCots, selectedMonth]);

  const filteredAdherents = useMemo(() => {
    if (statusFilter === 'all') return tabAdherents;
    return tabAdherents.filter(a => {
      const cot = (yearCots[a.id] || {})[selectedMonth];
      return (cot ? cot.status : 'non_pointe') === statusFilter;
    });
  }, [tabAdherents, statusFilter, yearCots, selectedMonth]);

  const tabCounts = useMemo(() => {
    const r = {};
    PAY_TABS.forEach(t => { r[t.id] = actifs.filter(t.filter).length; });
    return r;
  }, [actifs]);

  const monthStats = useMemo(() => {
    const base = showAllKpi ? actifs : tabAdherents;
    const eligibles = base.filter(adh => moisActifs(adh, yearCots).includes(selectedMonth));
    let paid = 0, unpaid = [], unpaid_dp = 0, unpaid_aa = 0, unpaid_am = 0, pending = 0, collected = 0;
    eligibles.forEach(adh => {
      const c = (yearCots[adh.id] || {})[selectedMonth];
      const s = c?.status || 'non_pointe';
      if (s === 'paye') { paid++; collected += (adh.montant || 0); }
      else if (s === 'defaut_provision') { unpaid_dp++; unpaid.push({ adh, status: s, motif: c?.motif || '' }); }
      else if (s === 'arret_adhesion')   { unpaid_aa++; unpaid.push({ adh, status: s, motif: c?.motif || '' }); }
      else if (s === 'autre_motif')      { unpaid_am++; unpaid.push({ adh, status: s, motif: c?.motif || '' }); }
      else pending++;
    });
    const expected = eligibles.reduce((s, a) => s + (a.montant || 0), 0);
    const taux = expected > 0 ? Math.round(collected / expected * 100) : 0;
    return { paid, unpaid, unpaid_dp, unpaid_aa, unpaid_am, pending, collected, expected, taux, count: eligibles.length };
  }, [actifs, tabAdherents, yearCots, selectedMonth, showAllKpi]);

  const yearStats = useMemo(() => {
    let total = 0, paid = 0, collected = 0;
    actifs.forEach(adh => {
      const months = moisActifs(adh, yearCots);
      total += months.length;
      months.forEach(m => {
        const c = (yearCots[adh.id] || {})[m];
        if (c?.status === 'paye') { paid++; collected += (adh.montant || 0); }
      });
    });
    const expected = actifs.reduce((s, a) => s + montantAnnuel(a), 0);
    const taux = expected > 0 ? Math.round(collected / expected * 100) : 0;
    return { total, paid, collected, expected, taux };
  }, [actifs, yearCots]);

  const monthSelectorStats = useMemo(() => {
    const base = showAllKpi ? actifs : tabAdherents;
    return Array.from({ length: 12 }, (_, mi) => {
      const m = mi + 1;
      const eligibles = base.filter(a => moisActifs(a, yearCots).includes(m));
      const paid = eligibles.filter(a => (yearCots[a.id]?.[m]?.status) === 'paye').length;
      return { paid, total: eligibles.length };
    });
  }, [actifs, tabAdherents, yearCots, showAllKpi]);

  const bulkApply = (status, motif = '') => {
    tabAdherents.filter(a => selectedIds.includes(a.id)).forEach(a => {
      setCotisation(selectedYear, a.id, selectedMonth, status, motif);
    });
    setSelectedIds([]);
    setBulkModal(false);
    setBulkPending(null);
    setBulkMotif('');
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = (e) => {
    e.stopPropagation();
    setSelectedIds(prev => prev.length === filteredAdherents.length ? [] : filteredAdherents.map(a => a.id));
  };

  const openStatusModal = (adh) => {
    setStatusModal(adh);
    setMotifText(getMotif(adh));
    setPendingStatus(null);
  };

  const applyStatus = (status, motif = '') => {
    if (!statusModal) return;
    setCotisation(selectedYear, statusModal.id, selectedMonth, status, motif);
    setStatusModal(null);
    setMotifText('');
    setPendingStatus(null);
  };

  const handleStatusChoice = (status) => {
    if (NEEDS_MOTIF.includes(status)) {
      setPendingStatus(status);
    } else {
      applyStatus(status);
    }
  };

  const handleExport = () => {
    const rows = [['N°','Prénom','Nom','Structure','Mode','Jour','Périodicité','Montant','Statut','Motif']];
    actifs.forEach(adh => {
      rows.push([adh.numero, adh.prenom, adh.nom, etabName(adh.etablissementId),
        adh.modePaiement, adh.jourPrelevement || '—', adh.periodicite || '—',
        adh.montant, STATUS_LABELS[getStatus(adh)], getMotif(adh)]);
    });
    exportCSV(rows, `pointage_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">📅 Pointage</div>
          <div className="page-subtitle">Année {selectedYear}</div>
        </div>
        <div className="flex gap-2">
          <select className="form-control" style={{ width: 90 }} value={selectedYear} onChange={e => setYear(+e.target.value)}>
            {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {subView === 'mensuel' && <button className="btn btn-outline btn-sm" onClick={handleExport}>⬇️ CSV</button>}
          <button
            className="btn btn-sm"
            style={{ background:'#fff5f5', color:'#c62828', border:'1px solid #ef9a9a' }}
            onClick={() => setConfirmReset(true)}
          >
            ↩️ Réinitialiser les pointages
          </button>
          {subView === 'mensuel' && <button className="btn btn-outline btn-sm" onClick={() => printElement('pointage-print')}>🖨️ Imprimer</button>}
        </div>
      </div>

      {/* Sub-view toggle */}
      <div className="tabs mb-4">
        <button className={`tab-btn${subView === 'mensuel' ? ' active' : ''}`} onClick={() => setSubView('mensuel')}>
          📅 Pointage mensuel
        </button>
        <button className={`tab-btn${subView === 'annuel' ? ' active' : ''}`} onClick={() => setSubView('annuel')}>
          🗓️ Tableau annuel
        </button>
      </div>

      {/* ── ANNUEL VIEW ── */}
      {subView === 'annuel' && (
        <TableauAnnuel
          adherents={adherents}
          etablissements={etablissements}
          cotisations={cotisations}
          selectedYear={selectedYear}
          setYear={setYear}
          setCotisation={setCotisation}
          goToAdherent={goToAdherent}
        />
      )}

      {/* ── MENSUEL VIEW ── */}
      {subView === 'mensuel' && (
        <>
          {/* Month selector */}
          <div className="card mb-4">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              {MOIS.map((m, mi) => {
                const mnum = mi + 1;
                const { paid, total } = monthSelectorStats[mi];
                const pct = total > 0 ? paid / total : 0;
                const active = mnum === selectedMonth;
                const isPayMonth = validMonths ? validMonths.includes(mnum) : true;
                return (
                  <div key={mnum} onClick={() => { setSelectedMonth(mnum); setSelectedIds([]); }} style={{
                    cursor: 'pointer', borderRadius: 8,
                    border: active ? '2px solid #CC0000' : isPayMonth ? '1px solid #CC000044' : '1px solid #eee',
                    background: active ? '#fff5f5' : isPayMonth ? '#fff9f9' : '#fafafa',
                    padding: '7px 10px', minWidth: 72, textAlign: 'center', transition: 'all .15s',
                    opacity: validMonths && !isPayMonth ? 0.45 : 1
                  }}>
                    <div style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? '#CC0000' : isPayMonth ? '#c00' : '#aaa' }}>{m}</div>
                    {isPayMonth && <div style={{ fontSize: 9, color: '#CC0000', fontWeight: 700, marginBottom: 1 }}>✓</div>}
                    <div style={{ fontSize: 11, color: '#888', margin: '2px 0' }}>{paid}/{total}</div>
                    <div style={{ height: 3, borderRadius: 2, background: '#eee', overflow: 'hidden' }}>
                      <div style={{ width: `${pct * 100}%`, height: '100%', background: active ? '#CC0000' : '#4caf50', transition: 'width .3s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 12, color: '#888', flexShrink: 0 }}>Recouvrement {selectedYear} :</div>
              <div style={{ flex: 1 }}>
                <div className="progress">
                  <div className="progress-bar" style={{ width: `${yearStats.taux}%` }} />
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#888' }}>{yearStats.paid} / {yearStats.total} · {yearStats.taux}%</div>
            </div>
          </div>

          {/* ── Cumul annuel ── */}
          <div className="card mb-4" style={{ background: '#f8f9ff', border: '1px solid #e8eaf6' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#5c6bc0', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10 }}>
              📊 Cumul annuel {selectedYear}
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 10 }}>
              <div>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#3949ab' }}>{yearStats.paid}</span>
                <span style={{ fontSize: 13, color: '#7986cb', marginLeft: 4 }}>/ {yearStats.total} paiements attendus</span>
              </div>
              <div>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#3949ab' }}>{formatMontant(yearStats.collected)}</span>
                <span style={{ fontSize: 12, color: '#7986cb', marginLeft: 4 }}>collectés / {formatMontant(yearStats.expected)} objectif · {yearStats.taux}%</span>
              </div>
            </div>
            <div className="progress">
              <div className="progress-bar" style={{ width: `${yearStats.taux}%`, background: '#5c6bc0' }} />
            </div>
          </div>

          {/* ── Filtre KPI ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: '#666' }}>
              {showAllKpi ? 'Tous les modes de paiement' : `Filtré : ${currentTabDef.label}`}
            </span>
            {showAllKpi ? (
              <button onClick={() => setShowAllKpi(false)} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, border: '1px solid #CC0000', background: '#fff5f5', color: '#CC0000', cursor: 'pointer' }}>
                ← Retour au filtre
              </button>
            ) : (
              <button onClick={() => setShowAllKpi(true)} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, border: '1px solid #ccc', background: '#f5f5f5', color: '#666', cursor: 'pointer' }}>
                Voir tous
              </button>
            )}
          </div>

          {/* ── KPIs mensuels ── */}
          <div className="mb-4">
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              {/* Card 1 : cotisations reçues */}
              <div className="stat-card" style={{ flex: '1 1 160px', border: '1px solid #c8e6c9', background: '#f1f8e9' }}>
                <div className="stat-label" style={{ color: '#388e3c' }}>✅ Cotisations reçues ce mois</div>
                <div className="stat-value text-green">{monthStats.paid}</div>
                <div className="stat-sub">sur {monthStats.count} éligibles · {monthStats.taux}%</div>
              </div>
              {/* Section : situations à traiter */}
              <div style={{ flex: '2 1 280px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#e65100', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 6 }}>Situations à traiter</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div className="stat-card" style={{ flex: 1, border: '1px solid #ffe0b2', background: '#fff8f3' }}>
                    <div className="stat-label" style={{ fontSize: 10 }}>⚠️ Défauts paiement</div>
                    <div className="stat-value" style={{ fontSize: 22, color: '#e65100' }}>{monthStats.unpaid_dp}</div>
                  </div>
                  <div className="stat-card" style={{ flex: 1, border: '1px solid #ffcdd2', background: '#fff5f5' }}>
                    <div className="stat-label" style={{ fontSize: 10 }}>🛑 Arrêts adhésion</div>
                    <div className="stat-value" style={{ fontSize: 22, color: '#c62828' }}>{monthStats.unpaid_aa}</div>
                  </div>
                  <div className="stat-card" style={{ flex: 1, border: '1px solid #e0e0e0', background: '#fafafa' }}>
                    <div className="stat-label" style={{ fontSize: 10 }}>📝 Autres motifs</div>
                    <div className="stat-value" style={{ fontSize: 22, color: '#555' }}>{monthStats.unpaid_am}</div>
                  </div>
                </div>
              </div>
              {/* Card : non pointés */}
              <div className="stat-card" style={{ flex: '1 1 140px', border: '1px solid #e0e0e0' }}>
                <div className="stat-label">🔲 Non pointés ce mois</div>
                <div className="stat-value text-gray">{monthStats.pending}</div>
                <div className="stat-sub">sans saisie</div>
              </div>
            </div>
            {/* Card : collecté */}
            <div className="stat-card" style={{ border: '1px solid #ddd' }}>
              <div className="stat-label">💶 Collecté en {MOIS[selectedMonth - 1]}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <div className="stat-value">{formatMontant(monthStats.collected)}</div>
                <div className="stat-sub">Objectif : {formatMontant(monthStats.expected)} ({monthStats.count} éligibles) · Taux : {monthStats.taux}%</div>
              </div>
              <div className="progress mt-2">
                <div className="progress-bar green" style={{ width: `${monthStats.taux}%` }} />
              </div>
            </div>
          </div>

          {/* Situations à traiter */}
          {monthStats.unpaid.length > 0 && (
            <div className="card mb-4">
              <div className="card-header">
                <span className="card-title">📋 Situations à traiter — {MOIS[selectedMonth - 1]} {selectedYear}</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Adhérent</th><th>Structure</th><th>Statut</th><th>Motif</th></tr>
                  </thead>
                  <tbody>
                    {monthStats.unpaid.map(({ adh, status, motif }) => (
                      <tr key={adh.id} style={{ cursor: 'pointer' }} onClick={() => openStatusModal(adh)}>
                        <td><strong>{adh.prenom} {adh.nom}</strong></td>
                        <td>{etabName(adh.etablissementId)}</td>
                        <td><span className={`badge ${STATUS_BADGE_CLASS[status]}`}>{STATUS_LABELS[status]}</span></td>
                        <td style={{ color: '#888' }}>{motif || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payment mode pill-tabs */}
          <div className="card">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid #f0f0f0' }}>
              {PAY_TABS.map(t => {
                const cnt = tabCounts[t.id];
                if (cnt === 0) return null;
                const active = payTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => { setPayTab(t.id); setStatusFilter('all'); }}
                    style={{
                      padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                      background: active ? '#CC0000' : '#f0f0f0',
                      color: active ? '#fff' : '#555',
                      fontWeight: active ? 700 : 500,
                      fontSize: 13, transition: 'all .15s',
                      display: 'flex', alignItems: 'center', gap: 6
                    }}
                  >
                    {t.label}
                    <span style={{
                      background: active ? 'rgba(255,255,255,.3)' : '#ddd',
                      color: active ? '#fff' : '#777',
                      borderRadius: 10, fontSize: 11, padding: '0 6px', fontWeight: 700, lineHeight: '18px'
                    }}>{cnt}</span>
                  </button>
                );
              })}
            </div>

            <div id="pointage-print">
              {/* Bulk action bar */}
              {selectedIds.length > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#fff8e1', borderRadius:8, border:'1px solid #ffe082', marginBottom:12, flexWrap:'wrap' }}>
                  <span style={{ fontWeight:700, fontSize:13, color:'#795548' }}>☑️ {selectedIds.length} sélectionné{selectedIds.length > 1 ? 's' : ''}</span>
                  <span style={{ flex:1 }} />
                  {STATUS_OPTIONS.map(opt => (
                    <button key={opt.status} onClick={() => {
                      if (['defaut_provision','arret_adhesion','autre_motif'].includes(opt.status)) { setBulkPending(opt.status); setBulkModal(true); }
                      else bulkApply(opt.status);
                    }} style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${opt.border}`, background:opt.bg, color:opt.color, fontWeight:700, fontSize:12, cursor:'pointer' }}>
                      {opt.label}
                    </button>
                  ))}
                  <button onClick={() => setSelectedIds([])} style={{ padding:'5px 10px', borderRadius:6, border:'1px solid #ddd', background:'#fff', color:'#888', fontSize:12, cursor:'pointer' }}>✕</button>
                </div>
              )}
              <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
                <strong style={{ color: '#333' }}>{currentTabDef.label}</strong>
                {' — '}{MOIS[selectedMonth - 1]} {selectedYear}
                {' · '}<strong>{tabAdherents.length} adhérent{tabAdherents.length !== 1 ? 's' : ''}</strong>
                <span style={{ fontSize: 12, color: '#aaa', marginLeft: 8 }}>(cliquer sur une ligne pour saisir le statut)</span>
              </div>
              {/* Filtre statut */}
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
                {[
                  { id:'all',              label:'Tous',          color:'#555',    bg:'#f0f0f0', active:'#555'    },
                  { id:'paye',             label:'✅ Payé',        color:'#2e7d32', bg:'#e8f5e9', active:'#2e7d32' },
                  { id:'non_pointe',       label:'Non pointé',    color:'#888',    bg:'#fafafa', active:'#607d8b' },
                  { id:'defaut_provision', label:'⚠️ Défaut',      color:'#e65100', bg:'#fff3e0', active:'#e65100' },
                  { id:'arret_adhesion',   label:'🛑 Arrêt',       color:'#c62828', bg:'#ffebee', active:'#c62828' },
                  { id:'autre_motif',      label:'📝 Autre motif', color:'#555',    bg:'#f5f5f5', active:'#555'    },
                ].map(f => (statusCounts[f.id] > 0 || f.id === 'all') ? (
                  <button key={f.id} onClick={() => setStatusFilter(f.id)} style={{
                    padding:'4px 12px', borderRadius:16, fontSize:12, cursor:'pointer', border:'none',
                    fontWeight: statusFilter === f.id ? 700 : 500,
                    background: statusFilter === f.id ? f.active : f.bg,
                    color: statusFilter === f.id ? '#fff' : f.color,
                    display:'flex', alignItems:'center', gap:5, transition:'all .15s'
                  }}>
                    {f.label}
                    <span style={{ background:'rgba(0,0,0,.15)', borderRadius:8, padding:'0 5px', fontSize:11 }}>{statusCounts[f.id]}</span>
                  </button>
                ) : null)}
              </div>

              {filteredAdherents.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">👥</div>
                  <div className="empty-state-text">Aucun adhérent dans ce groupe.</div>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0 8px 0', borderBottom: '2px solid #f0f0f0', fontSize: 11, fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: .4 }}>
                    <div style={{ width: 20, flexShrink: 0 }}>
                      <input type="checkbox" style={{ accentColor:'#CC0000', cursor:'pointer' }}
                        checked={selectedIds.length === filteredAdherents.length && filteredAdherents.length > 0}
                        onChange={toggleAll} onClick={e => e.stopPropagation()} />
                    </div>
                    <div style={{ width: 34 }} />
                    <div style={{ flex: 1 }}>Adhérent</div>
                    <div style={{ width: 150 }}>Structure</div>
                    <div style={{ width: 65, textAlign: 'right' }}>Montant</div>
                    <div style={{ width: 110, textAlign: 'center' }}>Statut</div>
                  </div>
                  {filteredAdherents.map(adh => {
                    const status = getStatus(adh);
                    const motif = getMotif(adh);
                    const opt = STATUS_OPTIONS.find(o => o.status === status);
                    return (
                      <div
                        key={adh.id}
                        onClick={() => openStatusModal(adh)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 0', borderBottom: '1px solid #f8f8f8',
                          cursor: 'pointer', borderRadius: 4, transition: 'background .1s',
                          background: selectedIds.includes(adh.id) ? '#fff8e1' : ''
                        }}
                        onMouseOver={e => { if (!selectedIds.includes(adh.id)) e.currentTarget.style.background = '#fafafa'; }}
                        onMouseOut={e => { if (!selectedIds.includes(adh.id)) e.currentTarget.style.background = ''; }}
                      >
                        <div style={{ width: 20, flexShrink: 0 }} onClick={e => toggleSelect(adh.id, e)}>
                          <input type="checkbox" style={{ accentColor:'#CC0000', cursor:'pointer' }}
                            checked={selectedIds.includes(adh.id)} onChange={() => {}} />
                        </div>
                        <div className="adh-avatar" style={{ width: 34, height: 34, fontSize: 12, flexShrink: 0 }}>
                          {initials(adh.prenom, adh.nom)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{ fontWeight: 600, fontSize: 13, color: '#CC0000', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#CC000044' }}
                            onClick={e => { e.stopPropagation(); goToAdherent && goToAdherent(adh.id); }}
                          >{adh.prenom} {adh.nom}</div>
                          <div style={{ fontSize: 11, color: '#aaa' }}>{adh.numero}</div>
                        </div>
                        <div style={{ width: 150, fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {etabName(adh.etablissementId)}
                        </div>
                        <div style={{ width: 75, fontSize: 12, color: '#555', textAlign: 'right', fontWeight: 600, display:'flex', alignItems:'center', justifyContent:'flex-end', gap:4 }}>
                          <span>{adh.montant ? Number(adh.montant).toFixed(2) + ' €' : '—'}</span>
                          <span
                            onClick={e => { e.stopPropagation(); setMontantModal(adh); setMontantValue(String(adh.montant || '')); }}
                            style={{ cursor:'pointer', fontSize:10, color:'#bbb', padding:'1px 3px', borderRadius:3, lineHeight:1 }}
                            title="Modifier le montant"
                          >✏️</span>
                        </div>
                        <div style={{ width: 110, textAlign: 'center' }}>
                          {status === 'non_pointe' ? (
                            <span style={{ fontSize: 12, color: '#ccc', fontStyle: 'italic' }}>— non pointé</span>
                          ) : (
                            <span
                              className={`badge ${STATUS_BADGE_CLASS[status]}`}
                              style={{ fontSize: 11 }}
                            >
                              {STATUS_LABELS[status]}
                            </span>
                          )}
                          {motif && (
                            <div style={{ fontSize: 10, color: '#aaa', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={motif}>
                              {motif}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Status choice modal ── */}
      {statusModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Saisie du pointage</div>
                <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                  {statusModal.prenom} {statusModal.nom} — {MOIS[selectedMonth - 1]} {selectedYear}
                </div>
              </div>
              <button className="btn-icon" onClick={() => { setStatusModal(null); setPendingStatus(null); setMotifText(''); }}>✕</button>
            </div>

            {!pendingStatus ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '16px 0' }}>
                  {STATUS_OPTIONS.map(({ status, label, bg, color, border }) => {
                    const isCurrent = getStatus(statusModal) === status;
                    return (
                      <button
                        key={status}
                        onClick={() => handleStatusChoice(status)}
                        style={{
                          padding: '14px 12px', borderRadius: 10,
                          border: `2px solid ${isCurrent ? color : border}`,
                          background: isCurrent ? bg : '#fff',
                          color, fontWeight: 700, fontSize: 13,
                          cursor: 'pointer', transition: 'all .15s', textAlign: 'left'
                        }}
                        onMouseOver={e => { e.currentTarget.style.background = bg; e.currentTarget.style.borderColor = color; }}
                        onMouseOut={e => { e.currentTarget.style.background = isCurrent ? bg : '#fff'; e.currentTarget.style.borderColor = isCurrent ? color : border; }}
                      >
                        {label}
                        {isCurrent && <div style={{ fontSize: 10, fontWeight: 400, marginTop: 2, opacity: .7 }}>✓ statut actuel</div>}
                      </button>
                    );
                  })}
                </div>
                {getStatus(statusModal) !== 'non_pointe' && (
                  <button
                    onClick={() => applyStatus('non_pointe', '')}
                    style={{ width: '100%', padding: '8px', borderRadius: 8, border: '1px solid #eee', background: '#fafafa', color: '#999', fontSize: 12, cursor: 'pointer', marginBottom: 4 }}
                  >
                    ↩️ Réinitialiser (non pointé)
                  </button>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, color: '#555', margin: '14px 0 8px' }}>
                  <strong>{STATUS_LABELS[pendingStatus]}</strong> — saisir le motif :
                </div>
                <div className="form-group">
                  <textarea
                    className="form-control"
                    value={motifText}
                    onChange={e => setMotifText(e.target.value)}
                    placeholder="Ex : chèque retourné, démission, congé parental..."
                    rows={3}
                    autoFocus
                  />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline" onClick={() => setPendingStatus(null)}>← Retour</button>
                  <button className="btn btn-primary" onClick={() => applyStatus(pendingStatus, motifText)}>Valider</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Montant edit modal */}
      {montantModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 340 }}>
            <div className="modal-header">
              <span className="modal-title">Montant — {montantModal.prenom} {montantModal.nom}</span>
              <button className="btn-icon" onClick={() => setMontantModal(null)}>✕</button>
            </div>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label">Nouveau montant (€/échéance)</label>
              <input className="form-control" type="number" step="0.01" min="0" autoFocus
                value={montantValue} onChange={e => setMontantValue(e.target.value)} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setMontantModal(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={() => {
                if (saveAdherent) saveAdherent({ ...montantModal, montant: parseFloat(montantValue) || 0 });
                setMontantModal(null);
              }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm reset all pointages */}
      {confirmReset && (
        <div className="modal-overlay" onClick={() => setConfirmReset(false)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">↩️ Réinitialiser tous les pointages ?</div>
              <button className="btn-icon" onClick={() => setConfirmReset(false)}>✕</button>
            </div>
            <p style={{ fontSize: 14, color: '#6b7280', margin: '12px 0' }}>
              Tous les pointages de l'année <strong>{selectedYear}</strong> passeront en <strong>non pointé</strong>.
              Les statuts Payé, Défaut, Arrêt… seront effacés.
            </p>
            <p style={{ fontSize: 13, color: '#c62828', background: '#fff5f5', borderRadius: 6, padding: '8px 12px' }}>
              ⚠️ Cette action est irréversible.
            </p>
            <div className="modal-footer" style={{ marginTop: 16 }}>
              <button className="btn btn-outline" onClick={() => setConfirmReset(false)}>Annuler</button>
              <button
                className="btn btn-primary"
                style={{ background: '#c62828' }}
                onClick={() => { resetAllCotisationsYear(selectedYear); setConfirmReset(false); }}
              >
                ↩️ Confirmer la réinitialisation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk motif modal */}
      {bulkModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <span className="modal-title">{STATUS_OPTIONS.find(o => o.status === bulkPending)?.label} — {selectedIds.length} adhérent{selectedIds.length > 1 ? 's' : ''}</span>
              <button className="btn-icon" onClick={() => { setBulkModal(false); setBulkPending(null); setBulkMotif(''); }}>✕</button>
            </div>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label">Motif (optionnel)</label>
              <textarea className="form-control" value={bulkMotif} onChange={e => setBulkMotif(e.target.value)}
                placeholder="Ex : prélèvement rejeté, démission..." rows={3} autoFocus />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setBulkModal(false); setBulkPending(null); }}>Annuler</button>
              <button className="btn btn-primary" onClick={() => bulkApply(bulkPending, bulkMotif)}>Appliquer à tous</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
